import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  CreateEquipmentGroupDto,
  EquipmentGroupDetailsType,
  EquipmentGroupListItemType,
  TestNotificationResponse,
  UpdateEquipmentGroupDto,
} from '../../types/equipment-group/equipment-group.type';
import { EquipmentGroupTypeListItemType } from '../../types/equipment-group/equipment-group-type/equipment-group-type.type';
import { EQUIPMENT_GROUP_PREVIEW_HASH_TYPE } from '../../utils/constants';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { LocationType } from '../../types/location/location.type';

export const postEquipmentGroupDetails = createAsyncThunk<
  EquipmentGroupDetailsType,
  { data: CreateEquipmentGroupDto },
  { state: RootReducerState }
>('equipmentGroups/postEquipmentGroupDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<EquipmentGroupDetailsType>(
      `${process.env.REACT_APP_API_URL}/equipment-group`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating equipment group!' });
  }
});

export const getEquipmentGroupsList = createAsyncThunk<
  PaginationType<EquipmentGroupListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('equipmentGroups/getEquipmentGroupsList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<EquipmentGroupListItemType>>(
      `${process.env.REACT_APP_API_URL}/equipment-group${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching equipment groups!' });
  }
});

export const getEquipmentGroupTypesList = createAsyncThunk<
  Array<EquipmentGroupTypeListItemType>,
  void,
  { state: RootReducerState }
>('equipmentGroups/getEquipmentGroupTypesList', async (params, thunkAPI) => {
  try {
    const response = await axios.get<Array<EquipmentGroupTypeListItemType>>(
      `${process.env.REACT_APP_API_URL}/equipment-group/types`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching equipment group types!' });
  }
});

export const getEquipmentGroupDetails = createAsyncThunk<
  EquipmentGroupDetailsType,
  { id: number },
  {
    state: RootReducerState;
  }
>('equipmentGroups/getEquipmentGroupDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<EquipmentGroupDetailsType>(
      `${process.env.REACT_APP_API_URL}/equipment-group/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching equipment group!' });
  }
});

export const patchEquipmentGroupDetails = createAsyncThunk<
  EquipmentGroupDetailsType,
  { id: number; data: UpdateEquipmentGroupDto },
  { state: RootReducerState }
>('equipmentGroups/patchEquipmentGroupDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<EquipmentGroupDetailsType>(
      `${process.env.REACT_APP_API_URL}/equipment-group/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating equipment group!' });
  }
});

export const deleteEquipmentGroupDetails = createAsyncThunk<
  EquipmentGroupDetailsType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('equipmentGroups/deleteEquipmentGroupDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<EquipmentGroupDetailsType>(
      `${process.env.REACT_APP_API_URL}/equipment-group/${id}`
    );
    if (fetchList) thunkAPI.dispatch(getEquipmentGroupsList(thunkAPI.getState().equipmentGroupsReducer.egsRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting equipment group!' });
  }
});

export const getPublicEquipmentGroupsDetails = createAsyncThunk<
  LocationType & { equipment_group: EquipmentGroupDetailsType },
  { hash: string; type: string },
  { state: RootReducerState }
>('equipmentGroups/getPublicEquipmentGroupsDetails', async ({ hash, type }, thunkAPI) => {
  try {
    let url;
    Object.values(EQUIPMENT_GROUP_PREVIEW_HASH_TYPE).forEach(key => {
      if (type === key) {
        url = `${process.env.REACT_APP_API_URL}/${key}/public/${hash}`;
        if (key === 'router') url = `${process.env.REACT_APP_API_URL}/routers/public/${hash}`;
      }
    });

    const response = await axios.get<LocationType & { equipment_group: EquipmentGroupDetailsType }>(url);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching equipment group!' });
  }
});

export const postTestNotification = createAsyncThunk<
  TestNotificationResponse,
  { id: number; data: { sms?: boolean; email?: boolean; webhook?: boolean } },
  { state: RootReducerState }
>('equipmentGroups/postTestNotification', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/equipment-group/${id}/test-notification`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending test notification!' });
  }
});
